// ユーザー管理サービス
// Settings画面のユーザー管理機能の実装

import logger from '../utils/logger';
import { 
  UserInfo, 
  UserRole, 
  UserStatus, 
  UserPermissions, 
  UserInvitation, 
  UserStats,
  UserValidationResult,
  UserFilterOptions,
  DEFAULT_PERMISSIONS
} from '../types/userManagement';

class UserManagementService {
  private users: UserInfo[] = [];
  private invitations: UserInvitation[] = [];
  private initialized = false;

  // 初期化 - サンプルユーザーデータ作成
  async initialize(): Promise<void> {
    if (this.initialized) return;

    // サンプルユーザーデータ
    this.users = [
      {
        id: 'user-001',
        basicInfo: {
          firstName: '太郎',
          lastName: '田中',
          email: 'owner@salonhub.jp',
        },
        access: {
          role: 'owner' as UserRole,
          status: 'active',
          permissions: DEFAULT_PERMISSIONS.owner,
          joinDate: new Date('2023-01-01'),
          lastLogin: new Date(),
        },
        settings: {
          emailNotifications: true,
          pushNotifications: true,
          language: 'ja',
          timezone: 'Asia/Tokyo',
        },
        createdAt: new Date('2023-01-01'),
        updatedAt: new Date(),
        updatedBy: 'system',
      },
      {
        id: 'user-002',
        basicInfo: {
          firstName: '花子',
          lastName: '佐藤',
          email: 'manager@salonhub.jp',
        },
        access: {
          role: 'owner' as UserRole,
          status: 'active',
          permissions: DEFAULT_PERMISSIONS.owner,
          joinDate: new Date('2023-02-15'),
          lastLogin: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2時間前
        },
        settings: {
          emailNotifications: true,
          pushNotifications: false,
          language: 'ja',
          timezone: 'Asia/Tokyo',
        },
        createdAt: new Date('2023-02-15'),
        updatedAt: new Date('2023-02-15'),
        updatedBy: 'user-001',
      },
      {
        id: 'user-003',
        basicInfo: {
          firstName: '次郎',
          lastName: '鈴木',
          email: 'staff1@salonhub.jp',
        },
        access: {
          role: 'staff' as UserRole,
          status: 'active',
          permissions: DEFAULT_PERMISSIONS.staff,
          joinDate: new Date('2023-03-01'),
          lastLogin: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1日前
        },
        settings: {
          emailNotifications: false,
          pushNotifications: true,
          language: 'ja',
          timezone: 'Asia/Tokyo',
        },
        createdAt: new Date('2023-03-01'),
        updatedAt: new Date('2023-03-01'),
        updatedBy: 'user-002',
      },
      {
        id: 'user-004',
        basicInfo: {
          firstName: '美咲',
          lastName: '山田',
          email: 'staff2@salonhub.jp',
        },
        access: {
          role: 'staff' as UserRole,
          status: 'pending',
          permissions: DEFAULT_PERMISSIONS.staff,
          joinDate: new Date('2023-04-01'),
          lastLogin: undefined,
        },
        invitation: {
          invitedBy: 'user-001',
          invitedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7日前
        },
        settings: {
          emailNotifications: true,
          pushNotifications: true,
          language: 'ja',
          timezone: 'Asia/Tokyo',
        },
        createdAt: new Date('2023-04-01'),
        updatedAt: new Date('2023-04-01'),
        updatedBy: 'user-001',
      },
    ];

    // サンプル招待データ
    this.invitations = [
      {
        id: 'inv-001',
        email: 'newstaff@salonhub.jp',
        role: 'staff' as UserRole,
        permissions: DEFAULT_PERMISSIONS.staff,
        invitedBy: 'user-001',
        invitedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
        expiresAt: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000),
        token: 'invite-token-001',
        status: 'pending',
        message: '新しいスタッフとしてチームに参加していただけませんか？',
      },
    ];

    this.initialized = true;
  }

  // ユーザーリスト取得
  async getUsers(filters?: UserFilterOptions): Promise<UserInfo[]> {
    await this.initialize();
    
    let filteredUsers = [...this.users];

    if (filters) {
      if (filters.role) {
        filteredUsers = filteredUsers.filter(user => user.access.role === filters.role);
      }
      if (filters.status) {
        filteredUsers = filteredUsers.filter(user => user.access.status === filters.status);
      }
      if (filters.lastLogin) {
        filteredUsers = filteredUsers.filter(user => {
          if (!user.access.lastLogin) return false;
          const loginTime = user.access.lastLogin.getTime();
          if (filters.lastLogin!.from && loginTime < filters.lastLogin!.from.getTime()) return false;
          if (filters.lastLogin!.to && loginTime > filters.lastLogin!.to.getTime()) return false;
          return true;
        });
      }
    }

    return filteredUsers;
  }

  // ユーザー統計取得
  async getUserStats(): Promise<UserStats> {
    await this.initialize();

    const byRole = {
      owner: this.users.filter(u => u.access.role === 'owner').length,
      manager: this.users.filter(u => u.access.role === 'manager').length,
      staff: this.users.filter(u => u.access.role === 'staff').length,
      receptionist: this.users.filter(u => u.access.role === 'receptionist').length,
    };

    const byStatus = {
      active: this.users.filter(u => u.access.status === 'active').length,
      inactive: this.users.filter(u => u.access.status === 'inactive').length,
      pending: this.users.filter(u => u.access.status === 'pending').length,
    };

    const activeUsers = byStatus.active;
    const pendingInvitations = this.invitations.filter(inv => inv.status === 'pending').length;
    
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const lastWeekLogins = this.users.filter(user => 
      user.access.lastLogin && user.access.lastLogin > oneWeekAgo
    ).length;

    return {
      total: this.users.length,
      byRole,
      byStatus,
      activeUsers,
      pendingInvitations,
      lastWeekLogins,
    };
  }

  // ユーザー作成
  async createUser(userData: Omit<UserInfo, 'id' | 'createdAt' | 'updatedAt'>): Promise<string | null> {
    await this.initialize();

    try {
      // メールアドレス重複チェック
      const existingUser = this.users.find(user => 
        user.basicInfo.email === userData.basicInfo.email
      );
      if (existingUser) {
        throw new Error('このメールアドレスは既に使用されています');
      }

      const newId = `user-${Date.now()}`;
      const newUser: UserInfo = {
        ...userData,
        id: newId,
        createdAt: new Date(),
        updatedAt: new Date(),
        updatedBy: 'current-user', // 実際のユーザーIDに置き換え
      };

      this.users.push(newUser);
      return newId;
    } catch (error) {
      if (import.meta.env.DEV) {
        logger.error('Error creating user:', error);
      }
      return null;
    }
  }

  // ユーザー更新
  async updateUser(id: string, userData: Partial<UserInfo>): Promise<boolean> {
    await this.initialize();

    try {
      const userIndex = this.users.findIndex(user => user.id === id);
      if (userIndex === -1) {
        throw new Error('ユーザーが見つかりません');
      }

      // メールアドレス重複チェック（変更される場合）
      if (userData.basicInfo?.email) {
        const existingUser = this.users.find(user => 
          user.id !== id && user.basicInfo.email === userData.basicInfo?.email
        );
        if (existingUser) {
          throw new Error('このメールアドレスは既に使用されています');
        }
      }

      this.users[userIndex] = {
        ...this.users[userIndex],
        ...userData,
        updatedAt: new Date(),
        updatedBy: 'current-user',
      };

      return true;
    } catch (error) {
      if (import.meta.env.DEV) {
        logger.error('Error updating user:', error);
      }
      return false;
    }
  }

  // ユーザー削除
  async deleteUser(id: string): Promise<boolean> {
    await this.initialize();

    try {
      const userIndex = this.users.findIndex(user => user.id === id);
      if (userIndex === -1) {
        throw new Error('ユーザーが見つかりません');
      }

      // 管理者は削除不可
      if (this.users[userIndex].access.role === 'owner') {
        throw new Error('管理者アカウントは削除できません');
      }

      this.users.splice(userIndex, 1);
      return true;
    } catch (error) {
      if (import.meta.env.DEV) {
        logger.error('Error deleting user:', error);
      }
      return false;
    }
  }

  // 招待送信
  async sendInvitation(
    email: string, 
    role: UserRole, 
    message?: string,
    customPermissions?: Partial<UserPermissions>
  ): Promise<string | null> {
    await this.initialize();

    try {
      // 既存ユーザーチェック
      const existingUser = this.users.find(user => user.basicInfo.email === email);
      if (existingUser) {
        throw new Error('このメールアドレスのユーザーは既に存在します');
      }

      // 既存招待チェック
      const existingInvitation = this.invitations.find(inv => 
        inv.email === email && inv.status === 'pending'
      );
      if (existingInvitation) {
        throw new Error('このメールアドレスには既に招待が送信されています');
      }

      const invitationId = `inv-${Date.now()}`;
      const permissions = customPermissions ? 
        { ...DEFAULT_PERMISSIONS[role], ...customPermissions } : 
        DEFAULT_PERMISSIONS[role];

      const invitation: UserInvitation = {
        id: invitationId,
        email,
        role,
        permissions,
        invitedBy: 'current-user',
        invitedAt: new Date(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7日後に期限切れ
        token: `invite-token-${Date.now()}`,
        status: 'pending',
        message,
      };

      this.invitations.push(invitation);
      
      // 実際の実装では、メール送信処理を行う（本番環境では詳細ログを無効化）

      return invitationId;
    } catch (error) {
      if (import.meta.env.DEV) {
        logger.error('Error sending invitation:', error);
      }
      return null;
    }
  }

  // 招待一覧取得
  async getInvitations(): Promise<UserInvitation[]> {
    await this.initialize();
    return [...this.invitations];
  }

  // 招待キャンセル
  async cancelInvitation(invitationId: string): Promise<boolean> {
    await this.initialize();

    try {
      const invitationIndex = this.invitations.findIndex(inv => inv.id === invitationId);
      if (invitationIndex === -1) {
        throw new Error('招待が見つかりません');
      }

      this.invitations[invitationIndex].status = 'cancelled';
      return true;
    } catch (error) {
      if (import.meta.env.DEV) {
        logger.error('Error cancelling invitation:', error);
      }
      return false;
    }
  }

  // バルク操作
  async bulkUpdateUsers(
    userIds: string[], 
    operation: 'status_change' | 'role_change' | 'delete',
    data?: { status?: UserStatus; role?: UserRole }
  ): Promise<{ success: number; failed: number; errors: string[] }> {
    await this.initialize();

    const results = { success: 0, failed: 0, errors: [] as string[] };

    for (const userId of userIds) {
      try {
        switch (operation) {
          case 'status_change':
            if (data?.status) {
              const success = await this.updateUser(userId, {
                access: {
                  status: data.status,
                  role: 'staff' as const, // デフォルト値
                  permissions: DEFAULT_PERMISSIONS.staff,
                  joinDate: new Date()
                }
              });
              if (success) results.success++;
              else results.failed++;
            }
            break;

          case 'role_change':
            if (data?.role) {
              const success = await this.updateUser(userId, {
                access: {
                  role: data.role,
                  status: 'active' as const, // デフォルト値
                  permissions: DEFAULT_PERMISSIONS[data.role],
                  joinDate: new Date()
                }
              });
              if (success) results.success++;
              else results.failed++;
            }
            break;

          case 'delete': {
            const success = await this.deleteUser(userId);
            if (success) results.success++;
            else results.failed++;
            break;
          }
        }
      } catch (error) {
        results.failed++;
        results.errors.push(`User ${userId}: ${error}`);
      }
    }

    return results;
  }

  // ユーザーバリデーション
  validateUserData(userData: Partial<UserInfo>): UserValidationResult {
    const errors: { field: string; message: string }[] = [];

    // 基本情報チェック
    if (userData.basicInfo) {
      if (!userData.basicInfo.firstName?.trim()) {
        errors.push({ field: 'firstName', message: '名前は必須です' });
      }
      if (!userData.basicInfo.lastName?.trim()) {
        errors.push({ field: 'lastName', message: '姓は必須です' });
      }
      if (!userData.basicInfo.email?.trim()) {
        errors.push({ field: 'email', message: 'メールアドレスは必須です' });
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.basicInfo.email)) {
        errors.push({ field: 'email', message: '有効なメールアドレスを入力してください' });
      }
    }

    // 権限チェック
    if (userData.access) {
      if (!userData.access.role) {
        errors.push({ field: 'role', message: 'ロールは必須です' });
      }
      if (!userData.access.status) {
        errors.push({ field: 'status', message: 'ステータスは必須です' });
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// シングルトンインスタンス
export const userManagementService = new UserManagementService();
export default userManagementService;